/**
 * juliette-actions — Helpers du Suivi "promesses & actions" de Juliette.
 *
 * Source : table juliette_scheduled_actions (RLS team-scoped, SELECT + UPDATE
 * autorises a l'utilisateur). Y vivent :
 *  - les PROMESSES (action_type='deferred_topic', via defer_topic)
 *  - les RELANCES / prises de nouvelles (action_type='remind' | 'check_in', via schedule_action)
 *
 * Statuts : pending (a faire) / done (fait) / cancelled (abandonne).
 * Une action pending dont scheduled_at est passe = "en retard".
 */

import { supabase } from "@/integrations/supabase/client";

export type ActionStatus = "pending" | "done" | "cancelled";

export interface JulietteAction {
  id: string;
  contactId: string | null;
  contactName: string | null;
  actionType: string;
  description: string;
  scheduledAt: string;
  status: ActionStatus;
  doneAt: string | null;
}

/** Libelle lisible d'un type d'action. */
export function actionTypeLabel(type: string): string {
  switch (type) {
    case "deferred_topic":
      return "Promesse";
    case "remind":
      return "Relance";
    case "check_in":
      return "Prendre des nouvelles";
    default:
      return "Action";
  }
}

/**
 * Liste les actions a faire + faites recemment (pending & done), avec le nom du
 * contact resolu. Le tri par echeance laisse l'UI les ventiler (en retard / a
 * faire / fait).
 */
export async function listJulietteActions(): Promise<JulietteAction[]> {
  const { data, error } = await supabase
    .from("juliette_scheduled_actions")
    .select("id, contact_id, action_type, description, scheduled_at, status, done_at")
    .in("status", ["pending", "done"])
    .order("scheduled_at", { ascending: true })
    .limit(100);
  if (error) throw new Error(error.message);

  type Row = {
    id: string;
    contact_id: string | null;
    action_type: string;
    description: string;
    scheduled_at: string;
    status: ActionStatus;
    done_at: string | null;
  };
  const rows = (data ?? []) as Row[];

  // Resolution des noms de contacts en une requete.
  const ids = [...new Set(rows.map((r) => r.contact_id).filter((v): v is string => !!v))];
  const nameById = new Map<string, string>();
  if (ids.length > 0) {
    const { data: contacts } = await supabase
      .from("immo_contacts")
      .select("id, name, first_name, last_name")
      .in("id", ids);
    type C = { id: string; name: string | null; first_name: string | null; last_name: string | null };
    for (const c of (contacts ?? []) as C[]) {
      const label = (c.name ?? "").trim() || [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
      if (label) nameById.set(c.id, label);
    }
  }

  return rows.map((r) => ({
    id: r.id,
    contactId: r.contact_id,
    contactName: r.contact_id ? nameById.get(r.contact_id) ?? null : null,
    actionType: r.action_type,
    description: r.description,
    scheduledAt: r.scheduled_at,
    status: r.status,
    doneAt: r.done_at,
  }));
}

/** Marque une action comme faite. */
export async function markActionDone(id: string): Promise<void> {
  const { error } = await supabase
    .from("juliette_scheduled_actions")
    .update({ status: "done", done_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/** Abandonne (annule) une action. */
export async function cancelJulietteAction(id: string): Promise<void> {
  const { error } = await supabase
    .from("juliette_scheduled_actions")
    .update({ status: "cancelled", done_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}
